/** ***********
*
* <manage-family-users.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { DecodedToken, ModifyRoleDialogData, ModifyRoleDialogResult, RoleLevel, UserRoleLevel, FamilyUsersConfirmationDialogData, FamilyUsersConfirmationDialogResult } from './../../../models/user-right';
import { cloneDeep } from 'lodash';
import { debounceTime, map, startWith, filter, switchMap, tap, distinctUntilChanged } from 'rxjs/operators';
import { selectAllUsers$, selectMyRightsTree$, selectUserRightLoading$ } from 'src/app/store/selectors/user-right.selectors';
import { FamilyRight, ParentFamilyRight } from 'src/app/models/family';
import { UntypedFormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { RoleLevelEnum } from 'src/app/models/user-right';
import { UserRightService } from 'src/app/services/user-right.service';
import { selectAllUsersPerFamilyFilters$ } from 'src/app/store/selectors/filter.selectors';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { WorkerService } from 'src/app/services/workers.service';
import { workerMap } from 'src/app/workers/worker-rx';
import { FamilyUsersRoleLevelWorkerInput } from 'src/app/workers/models';
import { User } from 'src/app/models/user';
import { FilterService } from 'src/app/services/filter.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MatDialog } from '@angular/material/dialog';
import { DialogModifyRoleComponent } from '../dialog/dialog-modify-role/dialog-modify-role.component';
import { FamilyUsersConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { FamilyService } from 'src/app/services/family.service';
import { ParentFamilyService } from 'src/app/services/parent-family.service';
import { LoadParentFamilyRight, SuccessMyRightsTree } from 'src/app/store/actions/user-right.action';
import { getLevelSortingMagnitude } from '../_shared/sort-helper';

type RowDataType = UserRoleLevel & { updating: boolean; };

@Component({
    selector: 'app-manage-family-users',
    templateUrl: './manage-family-users.component.html',
    styleUrls: ['./manage-family-users.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class ManageFamilyUsersComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  public familyFormControl = new UntypedFormControl('', Validators.required);
  public parentFamilies$: Observable<ParentFamilyRight[]>;
  public users$: Observable<UserRoleLevel[]>;
  public readonly viewName = 'manageFamilyUsersView';
  public familiesLoading$: Observable<boolean>;
  public usersLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public dataSource = new MatTableDataSource<RowDataType>();
  public readonly displayedColumns: Array<keyof UserRoleLevel> = ['data', 'email', 'famRightLevel'];
  public selectedFamily!: FamilyRight;
  public currentUserId = '';
  public massUpdateDisabled = false;

  private _parentFamilies: ParentFamilyRight[] = [];
  private _subscription: Subscription = new Subscription();
  private _selectedFamily$: Subject<FamilyRight> = new Subject<FamilyRight>();

  constructor(
    private store: Store,
    private _userRightsService: UserRightService,
    private _workerService: WorkerService,
    private _cd: ChangeDetectorRef,
    private _filterService: FilterService,
    private _jwtHelper: JwtHelperService,
    private _dialog: MatDialog,
    private _parentFamilyService: ParentFamilyService,
    private _familyService: FamilyService,
  ) { }

  ngOnInit(): void {
    this._userRightsService.checkUserLoaded();
    this.familyFormControl.addValidators(this._validateFamily());
    this._initFamiliesFilter();
    this._initData();
    this._subscription.add(this.familyFormControl.valueChanges.pipe(
      filter((value: ParentFamilyRight | FamilyRight | string) => typeof value === 'object'),
    ).subscribe(
      (family: FamilyRight) => {
        this.selectedFamily = family;
        this._selectedFamily$.next(family);
      }
    ));
    this._subscription.add(this._filterService.workerBusy$.subscribe(
      computing => this.usersLoading$.next(computing)
    ));
    const token = localStorage.getItem('token');
    this.currentUserId = (<DecodedToken>this._jwtHelper.decodeToken(token)).preferred_username;
  }

  ngAfterViewInit(): void {
    this._subscription.add(this.usersLoading$.subscribe(
      () => this._cd.markForCheck()
    ));
    this._subscription.add(this.dataSource.connect().subscribe());
  }

  /**
   * Initialize the filter for the family
   */
  private _initFamiliesFilter() {
    this.familiesLoading$ = this.store.select(selectUserRightLoading$);
    const families$ = this.store.select(selectMyRightsTree$).pipe(
      // Keep only the parent families for which the user is at least a manager
      map((parentFamilies) => parentFamilies.filter(
        parentFamily => parentFamily.level && ![RoleLevelEnum.basic].includes(parentFamily.level as RoleLevelEnum)
      )),
      // Keep only the families for which the user is at least a manager
      map((parentFamilies) => parentFamilies.map(
        parentFamily => {
          parentFamily.families = parentFamily.families?.filter(
            family => family.level && [RoleLevelEnum.manager, RoleLevelEnum.administrator].includes(family.level as RoleLevelEnum)
          );
          return parentFamily;
        }
      ).filter(
        (parentFamily) => parentFamily.families?.length > 0
      )),
      // Sort by alphabetical order
      map((parentFamilies) => parentFamilies.sort(
        (a, b) => a.name.localeCompare(b.name)
      ).map(
        parentFamily => {
          parentFamily.families = parentFamily.families?.sort(
            (a, b) => a.name.localeCompare(b.name)
          );
          return parentFamily;
        }
      )),
    );
    // Store parent families to check user's input validity
    this._subscription.add(families$.subscribe(
      parentFamilies => this._parentFamilies = parentFamilies
    ));
    // Listen to the inputs in the control to filter the autocomplete options
    const search$ = this.familyFormControl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      distinctUntilChanged(),
      // The FamilyRight is passed when the option is selected, only emits strings
      map((value: string | FamilyRight): string => typeof value === 'string' ? value : ''),
    );
    // Set the values of the parent families for the autocomplete
    this.parentFamilies$ = combineLatest([
      families$,
      search$,
    ]).pipe(
      map(([parentFamilies, searched]) => {
        // Deep clone to avoid modifications of the parent families due to the same reference
        const filteredParentFamilies = cloneDeep(parentFamilies);
        if (!searched) {
          return filteredParentFamilies;
        }
        // Filter by regex (family name must include the characters, case insensitive)
        const searchRegex = new RegExp(`^.*${searched.split('').join('.*')}.*$`, 'iu');
        return filteredParentFamilies.filter(
          parentFamily => {
            const filteredFamilies = cloneDeep(parentFamily.families);
            parentFamily.families = filteredFamilies.filter(
              (family) => searchRegex.test(family.name)
            );
            return parentFamily;
          }
        ).filter(
          parentFamily => parentFamily.families?.length > 0
        );
      }),
    );
  }

  /**
   * Get the name of the family to display in the autocomplete
   * @param selectedFamily The family
   * @returns The name of the family
   */
  public getFamilyName(selectedFamily: ParentFamilyRight | FamilyRight) {
    return selectedFamily.name ?? '';
  }

  /**
   * Validator for the selected family
   * @returns The error if needed
   */
  private _validateFamily(): ValidatorFn {
    return (control: AbstractControl) => {
      const controlValue = typeof control.value === 'string' ? control.value : control.value.name;
      if (!controlValue) {
        return null;
      }
      const foundFamily = !!this._parentFamilies.find(
        parentFamily => {
          return parentFamily.name === controlValue || !!parentFamily.families?.find(
            family => family.name === controlValue
          );
        }
      );
      return foundFamily ? null : { invalidFamily: { value: controlValue } };
    };
  }

  /**
   * Initialize the data for the filters and the table
   */
  private _initData() {
    this.users$ = combineLatest([
      this.store.select(selectAllUsers$),
      this._selectedFamily$,
    ]).pipe(
      tap(() => {
        this.usersLoading$.next(true);
        this.dataSource.data = [];
      }),
      switchMap(([allUsers, selectedFamily]: [User[], FamilyRight]) => this._userRightsService.getUsersPerFamily$(selectedFamily.id).pipe(
        map((familyUsers) => {
          if (familyUsers.length === 0) {
            // Empty all lists
            allUsers = [];
          }
          return { allUsers, familyUsers };
        })
      )),
      workerMap<FamilyUsersRoleLevelWorkerInput['data'], UserRoleLevel[], never>(this._workerService.familyUsersRoleLevel),
    );
    this._subscription.add(this.users$.subscribe((users) => {
      if (users?.length > 0) {
        this._initTable();
      }
    }));
    this._fetchUserRights();
  }

  /**
   * Fetch the rights of the user
   */
  private _fetchUserRights() {
      this.store.dispatch(new LoadParentFamilyRight());
      this._subscription.add(
          forkJoin([
              this._parentFamilyService.getParentFamiliesRights(),
              this._familyService.getFamiliesRights()
          ]).subscribe(([parentFamiliesWithRights, familiesWithRights]) => {
              // dispatch
              this.store.dispatch(new SuccessMyRightsTree(parentFamiliesWithRights, familiesWithRights.createdData));
          })
      );
  }

  /**
   * Trigger a refresh of the data of the table
   */
  refreshData() {
    this._selectedFamily$.next(this.selectedFamily);
  }

  /**
   * Initialize the table and set its data
   */
  private _initTable() {
    this._subscription.add(this.store.select(selectAllUsersPerFamilyFilters$).subscribe(
      (users) => {
        this.massUpdateDisabled = users.some(user => user.fromGroupId);
        this.dataSource.data = users.map(
          user => ({ ...user, updating: false, })
        );
        this.dataSource.paginator = this.paginator;
      }
    ));
    this.dataSource.sort = this.sort;
    // Sorting
    this.dataSource.sortingDataAccessor = (item: UserRoleLevel, property) => {
      switch (property) {
        case 'data': return item.data;
        case 'email': return item.email;
        case 'famRightLevel': return getLevelSortingMagnitude(item.famRightLevel);
        default: return item[property];
      }
    };
  }

  /**
   * Called when the user change the paginator page
   * - Aims to auto scroll to the top of the table
   */
  public scrollUp() {
    const table = document.querySelector('.mat-table');
    table.scrollIntoView({ behavior: 'smooth' });
    window.scrollTo(0, 0);
  }

  /**
   * Find the parent family corresponding to the given asset family
   */
  private _getParentFamilyOfAssetFamily(family: FamilyRight): ParentFamilyRight | void {
    // Find the parent family for the user rights service
    return this._parentFamilies.find(
      ({ families }) => families?.find(
        ({ id }) => id === family.id
      )
    );
  }

  /**
   * Handle the change of the selected role
   * @param row The row corresponding to the change (includes the user information)
   * @param selectedRole The new selected role
   */
  public onRoleSelectionChange(row: RowDataType, selectedRole: RoleLevel) {
    if (row.famRightLevel === selectedRole) {
      return;
    }
    // Store previous value
    const originalLevel = row.famRightLevel;
    // Change the family right level so the component detect the change
    row.famRightLevel = selectedRole;
    // Find the parent family for the user rights service
    const parentFamily = <ParentFamilyRight>this._getParentFamilyOfAssetFamily(this.selectedFamily);
    // Select the action to do (Create / Update / Delete)
    let oActionObservable: Observable<any>;
    switch (originalLevel) {
      case RoleLevelEnum.basic:
        oActionObservable = this._userRightsService.createFamilyUserRole([row.userId], this.selectedFamily, parentFamily, selectedRole);
        break;
      default:
        switch (selectedRole) {
          case RoleLevelEnum.basic:
            oActionObservable = this._userRightsService.deleteFamilyUserRole([row.userId], this.selectedFamily, parentFamily);
            break;
          default:
            oActionObservable = this._userRightsService.updateFamilyUserRole([row.userId], this.selectedFamily, parentFamily, selectedRole);
        }
    }
    // Execute the action
    if (oActionObservable) {
      this._setRowsUpdatingStatus([row], true);
      // Execute the modification
      this._subscription.add(oActionObservable.subscribe({
        next: (result) => {
          if (result === 'null' || result === false) {
            // Error happened if `null` or `false`, but not `undefined`
            row.famRightLevel = originalLevel;
          }
        },
        complete: () => this._setRowsUpdatingStatus([row], false)
      }));
    }
  }

  /**
   * Fired after confirmation of role update
   * @param parentFamily The parentFamily to modify
   * @param family Optional - The family to modify
   */
  public onClickMassUpdateRole() {
    const parentFamily = <ParentFamilyRight>this._getParentFamilyOfAssetFamily(this.selectedFamily);
    const dialogData: ModifyRoleDialogData = {
      family: this.selectedFamily,
      parentFamily: parentFamily,
      requestTarget: this.selectedFamily,
      showBasicRole: true,
    };
    const dialog = this._dialog.open(DialogModifyRoleComponent, {
      data: dialogData,
    });

    this._subscription.add(
      dialog.afterClosed().subscribe((dialogResult: ModifyRoleDialogResult) => {
        switch (dialogResult?.action) {
          case 'cancel':
            // do nothing
            break;
          case 'update':
            this._confirmMassUpdate(dialogResult.level);
            break;
        }
      })
    );
  }

  /**
   * Opens the dialog to confirm the mass update and handle the answer
   * @param level The selected role level
   */
  private _confirmMassUpdate(level: RoleLevel) {
    const dialogData: FamilyUsersConfirmationDialogData = {
      role: level,
    };
    const dialog = this._dialog.open(FamilyUsersConfirmationDialogComponent, {
      data: dialogData,
    });
    this._subscription.add(
      dialog.afterClosed().subscribe((dialogResult: FamilyUsersConfirmationDialogResult) => {
        switch (dialogResult?.action) {
          case 'cancel':
            // do nothing
            break;
          case 'confirm':
            this._handleMassUpdate(level);
            break;
        }
      })
    );
  }

  /**
   * Mass update of each the users currently available in the table with the given role
   * @param level The selected role level
   */
  private _handleMassUpdate(level: RoleLevel) {
    // Mark each line as updating
    this._setRowsUpdatingStatus(this.dataSource.data, true);
    const parentFamily = <ParentFamilyRight>this._getParentFamilyOfAssetFamily(this.selectedFamily);
    let oActionObservable: Observable<any>;
    // Retrieve the users by each role level
    const usersByRole: { [K in RoleLevelEnum]: RowDataType[] } = this.dataSource.data.reduce(
      (users, row) => {
        users[row.famRightLevel].push(row);
        return users;
      }, {
      basic: [],
      reader: [],
      operator: [],
      manager: [],
      administrator: [],
      none: [],
    }
    );
    if (level === RoleLevelEnum.basic) {
      // Remove roles to all the users which have one
      const usersToDelete = [
        ...usersByRole[RoleLevelEnum.reader],
        ...usersByRole[RoleLevelEnum.operator],
        ...usersByRole[RoleLevelEnum.manager],
        ...usersByRole[RoleLevelEnum.administrator],
      ].map(({ userId }) => userId);
      // Prepare the delete action
      oActionObservable = this._userRightsService.deleteFamilyUserRole(
        usersToDelete,
        this.selectedFamily,
        parentFamily
      );
    } else {
      let usersToUpdate: RowDataType[];
      switch (level) {
        case RoleLevelEnum.reader:
          usersToUpdate = [
            ...usersByRole[RoleLevelEnum.operator],
            ...usersByRole[RoleLevelEnum.manager],
            ...usersByRole[RoleLevelEnum.administrator],
          ];
          break;
        case RoleLevelEnum.operator:
          usersToUpdate = [
            ...usersByRole[RoleLevelEnum.reader],
            ...usersByRole[RoleLevelEnum.manager],
            ...usersByRole[RoleLevelEnum.administrator],
          ];
          break;
        case RoleLevelEnum.manager:
          usersToUpdate = [
            ...usersByRole[RoleLevelEnum.reader],
            ...usersByRole[RoleLevelEnum.operator],
            ...usersByRole[RoleLevelEnum.administrator],
          ];
          break;
        case RoleLevelEnum.administrator:
          usersToUpdate = [
            ...usersByRole[RoleLevelEnum.reader],
            ...usersByRole[RoleLevelEnum.operator],
            ...usersByRole[RoleLevelEnum.manager],
          ];
          break;
      }
      // Prepare the create / update actions
      oActionObservable = forkJoin([
        this._userRightsService.createFamilyUserRole(usersByRole[RoleLevelEnum.basic].map(({ userId }) => userId), this.selectedFamily, parentFamily, level),
        this._userRightsService.updateFamilyUserRole(usersToUpdate.map(({ userId }) => userId), this.selectedFamily, parentFamily, level),
      ]);
    }
    if (oActionObservable) {
      // Execute the modifications
      this._subscription.add(oActionObservable.subscribe({
        error: () => this._setRowsUpdatingStatus(this.dataSource.data, false),
        complete: () => this.refreshData(),
      }));
    } else {
      this._setRowsUpdatingStatus(this.dataSource.data, false);
    }
  }

  /**
   * Set the updating status of the row (in asynchronous)
   * @param rows The rows to set the status
   * @param updating The status
   */
  private _setRowsUpdatingStatus(rows: RowDataType[], updating: boolean) {
    setTimeout(() => {
      rows.forEach(
        data => data.updating = updating
      );
      this._cd.markForCheck();
    }, 0);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

}
